import { useState } from 'react';
import { SEARCH_PUBLIC_PROFILES } from '../../../../graphql/queries/socialNetwork';
import type {
  SearchPublicProfilesQuery,
  SearchPublicProfilesQueryVariables,
} from '../../../../types/graphql-generated';
import { NETWORK_PAGE_SIZE } from '../../../../appConfig';
import { useInfiniteScrollQuery } from '../../../../hooks/useInfiniteScrollQuery';
import { PublicProfileCard } from '../../profile/components/PublicProfileCard';
import { NetworkSearchBox } from './NetworkSearchBox';
import { NetworkList } from './NetworkList';

// "Perfiles" segment of the "Red" tab. Before searching, it lists the people
// the user shares a fantasy league with.
export const ProfilesNetworkContent = () => {
  const [activeTerm, setActiveTerm] = useState('');

  const { data, loading, error, loadMore, resetPagination } = useInfiniteScrollQuery<
    SearchPublicProfilesQuery,
    SearchPublicProfilesQueryVariables
  >({
    query: SEARCH_PUBLIC_PROFILES,
    buildVariables: (offset) => ({ term: activeTerm, limit: NETWORK_PAGE_SIZE, offset }),
    pageSize: NETWORK_PAGE_SIZE,
    append: (previous, next) => {
      const previousPage = previous.searchPublicProfiles;
      const nextPage = next.searchPublicProfiles;
      if (!previousPage || !nextPage) return next;

      return {
        searchPublicProfiles: {
          ...nextPage,
          profiles: [...previousPage.profiles, ...nextPage.profiles],
        },
      };
    },
  });

  const page = data?.searchPublicProfiles;
  const profiles = page?.profiles ?? [];
  const totalCount = page?.totalCount ?? 0;
  const isSearching = activeTerm !== '';

  const handleSearch = (term: string) => {
    resetPagination();
    setActiveTerm(term);
  };

  return (
    <div className="space-y-4">
      <NetworkSearchBox
        placeholder="Buscar perfiles..."
        hint="Encuentra a tus amigos y conocidos, de tu equipo o de otros equipos, y sigue sus canales"
        loading={loading}
        onSearch={handleSearch}
      />

      <NetworkList
        loading={loading}
        hasError={Boolean(error)}
        errorMessage="No se han podido cargar los perfiles"
        count={profiles.length}
        hasMore={profiles.length < totalCount}
        onLoadMore={loadMore}
        emptyMessage={
          isSearching
            ? `No se han encontrado perfiles con «${activeTerm}»`
            : 'Todavía no compartes liga con nadie. Busca a otras personas por su nombre.'
        }
        heading={
          isSearching
            ? `${totalCount} ${totalCount === 1 ? 'perfil encontrado' : 'perfiles encontrados'}`
            : 'Gente de tus ligas'
        }
      >
        {profiles.map((profile) => (
          <PublicProfileCard
            key={profile.id}
            profile={profile}
            navigable
            profilePathPrefix="/app/main/red/perfiles"
          />
        ))}
      </NetworkList>
    </div>
  );
};
